import React, { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import {
  fetchFrequentProducts,
  fetchProductsOfStore,
  removeFrequentProduct,
  addFrequentProducts,
} from "../../../redux/Thunk/Product/ProductThunks";
import Delete from "../../common/icons/Delete";
import Search from "../../common/icons/Search";
import PlusCircle from "../../common/icons/PlusCircle";
import Loader from "../../Loader";
import { extentions } from "../../../utils/constants";
import { useOutsideClick } from "../../common/useOutsideClick";

const Frequent = () => {
  const dispatch = useDispatch();

  const storeProducts = useSelector((state) => state?.product?.storeProducts || []);
  const loading = useSelector((state) => state?.product?.loading);
  const frequentProducts = useSelector((state) => state?.product?.frequentProduct || []);
  const store = useSelector((state) => state?.store?.storeDetail?.store);
  const DID = useSelector((state) => state?.doctor?.doctorDetails?.doctor?.DID);

  const [searchTerm, setSearchTerm] = useState("");
  const [isDropdownOpen, setIsDropdownOpen] = useState(false);

  const handleClickOutside = (event) => {
    if (dropdownRef?.current && !dropdownRef?.current.contains(event?.target)) {
      setIsDropdownOpen(false);
    }
  };
  const dropdownRef = useOutsideClick(handleClickOutside);

  useEffect(() => {
    dispatch(fetchProductsOfStore());
  }, [dispatch]);

  useEffect(() => {
    const id = store?.SID ? store?.SID : DID ? DID : "";
    if (id) {
      setTimeout(() => {
        dispatch(fetchFrequentProducts(id));
      }, 100);
    }
  }, [dispatch, store?.SID, DID]);

  const handleSelectProduct = (product) => {
    // Add product directly via Redux
    dispatch(addFrequentProducts(product));
    setSearchTerm("");
    setIsDropdownOpen(false);
  };

  const handleRemoveProduct = (IID) => {
    if (IID) {
      dispatch(removeFrequentProduct(IID));
    }
  };

  const validFrequentProducts = Array.isArray(frequentProducts) ? frequentProducts : [];

  const filteredProducts = storeProducts?.filter((storeProd) => {
    // Exclude already added frequent products
    const isAlreadyAdded = validFrequentProducts.some(
      (freq) =>
        freq?.product?.IID === storeProd?.product?.IID ||
        freq?.IID === storeProd?.product?.IID
    );
    if (isAlreadyAdded) return false;

    // Filter by search term
    return storeProd?.product?.productName
      ?.toLowerCase()
      .includes(searchTerm?.toLowerCase());
  });

  return (
    <div className="w-full h-full mx-auto p-4 md:p-8">
      {loading ? <Loader loading={loading} /> : null}

      {/* Header section */}
      <div className="mb-8">
        <h1 className="text-2xl md:text-3xl font-bold text-gray-900 tracking-tight">
          Frequently Prescribed
        </h1>
        <p className="text-gray-500 mt-2 text-sm md:text-base max-w-3xl">
          Build and manage your quick-access list of commonly prescribed medicines.
          Products added here will instantly appear under the "Frequents" tab of the Prescription Maker.
        </p>
      </div>

      {/* Search & Add Section */}
      <div className="mb-10 relative z-20" ref={dropdownRef}>
        <div className="relative group shadow-sm transition-shadow hover:shadow-md rounded-2xl bg-white border border-gray-100 flex items-center pr-2">
          <div className="absolute inset-y-0 left-0 pl-5 flex items-center pointer-events-none">
            <Search className="h-5 w-5 text-gray-400 group-focus-within:text-primary-500 transition-colors" />
          </div>
          <input
            type="text"
            className="block w-full pl-14 pr-4 py-4 md:py-5 bg-transparent text-gray-900 placeholder-gray-400 focus:outline-none text-base md:text-lg border-none"
            placeholder="Search products to add to your frequents..."
            value={searchTerm}
            onChange={(e) => {
              setSearchTerm(e.target.value.trimStart());
              setIsDropdownOpen(true);
            }}
            onFocus={() => setIsDropdownOpen(true)}
          />
          <button
            className="hidden md:flex items-center gap-2 px-4 py-2 bg-primary-50 hover:bg-primary-100 text-primary-700 rounded-xl font-medium transition-colors"
            onClick={() => setIsDropdownOpen(true)}
          >
            <PlusCircle className="w-5 h-5" />
            Add
          </button>
        </div>

        {/* Search Results Dropdown */}
        {isDropdownOpen && searchTerm.length > 0 && (
          <div className="absolute z-50 mt-3 w-full bg-white rounded-2xl shadow-[0_10px_40px_-10px_rgba(0,0,0,0.15)] border border-gray-100 overflow-hidden transition-all top-[100%]">
            <div className="max-h-[350px] overflow-y-auto w-full py-2">
              {filteredProducts?.length > 0 ? (
                filteredProducts.map((product, idx) => (
                  <div
                    key={idx}
                    className="flex justify-between items-center cursor-pointer px-6 py-4 hover:bg-primary-50/50 transition-colors group border-b border-gray-50 last:border-0"
                    onClick={() => handleSelectProduct(product)}
                  >
                    <div className="flex-1">
                      <h3 className="text-gray-900 font-semibold text-[16px] md:text-[17px] capitalize group-hover:text-primary-600 transition-colors">
                        {product?.product?.productName}
                      </h3>
                      <div className="flex flex-wrap items-center gap-2 mt-1.5">
                        {product?.product?.type && (
                          <span className="bg-gray-100 text-gray-600 px-2 py-0.5 rounded text-xs font-semibold uppercase tracking-wider">
                            {product?.product?.type}
                          </span>
                        )}
                        <span className="text-gray-500 text-sm">
                          {product?.product?.uom} {product?.product?.type ? (extentions[product?.product?.type]?.uomExt || extentions[product?.product?.type]?.extention || "") : ""}
                        </span>
                        {product?.storeStock !== undefined && (
                          <span className={`text-xs font-semibold px-2 py-0.5 rounded ${Number(product?.storeStock) > 6 ? "bg-success-50 text-success-700" : Number(product?.storeStock) === 0 ? "bg-red-50 text-red-600" : "bg-yellow-50 text-yellow-700"}`}>
                            Stock: {product?.storeStock}
                          </span>
                        )}
                      </div>
                      <p className="text-gray-400 text-xs mt-2 truncate w-full max-w-[200px] md:max-w-md">
                        {product?.product?.drugs?.length > 0
                          ? product?.product?.drugs?.map((d) => d?.name || d).join(" • ")
                          : "No composition data"}
                      </p>
                    </div>
                    <div className="ml-4 pl-4 shrink-0 flex items-center border-l border-transparent group-hover:border-gray-100 transition-all h-full">
                      <button className="bg-white border border-gray-200 text-gray-700 font-medium px-4 py-2 rounded-lg group-hover:bg-primary-600 group-hover:text-white group-hover:border-primary-600 transition-all text-sm shadow-sm">
                        Add to List
                      </button>
                    </div>
                  </div>
                ))
              ) : (
                <div className="px-6 py-10 text-center bg-gray-50/30">
                  <p className="text-gray-500 font-medium">No new products found matching "{searchTerm}"</p>
                  <p className="text-gray-400 text-xs mt-2">The product might already be in your list, or check the spelling.</p>
                </div>
              )}
            </div>
          </div>
        )}
      </div>

      {/* List Section */}
      <div className="relative z-10 mt-8">
        <div className="flex items-center justify-between mb-6 border-b border-gray-100 pb-4">
          <h2 className="text-lg md:text-xl font-bold text-secondry-800 flex items-center gap-2">
            Your Prescription List
            <span className="bg-primary-50 text-primary-600 font-extrabold py-0.5 px-3 rounded-full text-xs">
              {validFrequentProducts.length}
            </span>
          </h2>
        </div>

        {validFrequentProducts.length > 0 ? (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 2xl:grid-cols-5 gap-4">
            {validFrequentProducts.map((item, index) => {
              const product = item?.product || item;
              const IID = product?.IID || "";
              const productName = product?.productName || "N/A";
              const type = product?.type || "Other";
              const manufacturer = product?.manufacturer || "N/A";
              const isCustom = String(IID).startsWith("CP");

              // Pricing logic consistent with rest of app
              const mrp = parseFloat(product?.pricing?.mrp || 0);
              const discount = parseFloat(product?.lookupMargin?.discount || product?.pricing?.discount || 0);

              // Drugs processing (clean braces/quotes)
              let drugsText = "N/A";
              if (product?.drugs) {
                const drugsArr = Array.isArray(product.drugs) ? product.drugs : [product.drugs];
                drugsText = drugsArr.map(d => (d?.name || d)).join(", ").replace(/[{}]/g, "").replace(/"/g, "");
              }

              return (
                <div
                  key={IID || index}
                  className="bg-white border border-primary-100/60 rounded-[20px] p-5 shadow-sm hover:shadow-xl hover:translate-y-[-4px] transition-all duration-400 cursor-default group flex flex-col justify-between relative overflow-hidden"
                >
                  <div>
                    {/* Header */}
                    <div className="flex justify-between items-center mb-4">
                      <div className="flex items-center gap-2">
                        <span className="px-2.5 py-0.5 bg-gray-50 text-gray-500 text-[9px] font-extrabold uppercase tracking-widest rounded-md border border-gray-100">
                          {type}
                        </span>
                        {isCustom && (
                          <span className="bg-amber-100 text-amber-800 text-[8px] font-extrabold px-1.5 py-0.5 rounded uppercase border border-amber-200">
                            Custom
                          </span>
                        )}
                      </div>

                      <button
                        onClick={() => handleRemoveProduct(IID)}
                        className="p-1.5 rounded-lg text-gray-300 hover:text-red-500 hover:bg-red-50 transition-all opacity-100 md:opacity-0 md:group-hover:opacity-100"
                        title="Remove from frequents"
                      >
                        <Delete className="w-4 h-4" />
                      </button>
                    </div>

                    {/* Bio */}
                    <div className="mb-4">
                      <h4 className="text-[16px] font-extrabold text-secondry-900 group-hover:text-primary-600 transition-colors line-clamp-1 mb-0.5 leading-tight">
                        {productName}
                      </h4>
                      <p className="text-[10px] font-extrabold text-secondry-300 uppercase tracking-widest leading-none mb-3">
                        {manufacturer}
                      </p>

                      <div className="pt-3 border-t border-gray-50">
                        <span className="inline-block text-[8.5px] font-extrabold text-primary-400 uppercase tracking-widest mb-1.5 px-1.5 py-0.5 bg-primary-50/50 rounded-md">
                          Composition
                        </span>
                        <p className="text-[12px] text-secondry-600 font-bold line-clamp-2 leading-snug italic min-h-[34px]">
                          {drugsText}
                        </p>
                      </div>
                    </div>
                  </div>

                  {/* Pricing and Stock */}
                  <div className="grid grid-cols-4 gap-2 pt-4 border-t border-gray-100">
                    <div className="flex flex-col">
                      <span className="text-[8.5px] text-primary-400 font-extrabold uppercase tracking-widest mb-1 leading-none">MRP</span>
                      <span className="text-[15px] font-extrabold text-secondry-800 leading-none truncate">
                        ₹{mrp.toLocaleString("en-IN")}
                      </span>
                    </div>

                    <div className="flex flex-col items-center">
                      <span className="text-[8.5px] text-primary-400 font-extrabold uppercase tracking-widest mb-1 leading-none">Factor</span>
                      <span className={`text-[15px] font-extrabold leading-none truncate ${product?.lookupMargin?.type || product?.pricing?.lookupMarginType ? "text-secondry-800" : "text-gray-300"}`}>
                        {product?.lookupMargin?.type || product?.pricing?.lookupMarginType || "N/A"}
                      </span>
                    </div>

                    <div className="flex flex-col items-center">
                      <span className="text-[8.5px] text-primary-400 font-extrabold uppercase tracking-widest mb-1 leading-none">Discount</span>
                      <span className={`text-[15px] font-extrabold leading-none truncate ${discount > 0 ? "text-secondry-800" : "text-gray-300"}`}>
                        {discount > 0 ? `${discount}%` : "0%"}
                      </span>
                    </div>

                    <div className="flex flex-col items-end">
                      <span className="text-[8.5px] text-primary-400 font-extrabold uppercase tracking-widest mb-1 leading-none">Stock</span>
                      <span className={`text-[15px] font-extrabold leading-none truncate ${Number(product.stock) > 6 ? "text-success-600" : Number(product.stock) === 0 ? "text-red-500" : "text-yellow-700"}`}>
                        {product.stock || 0}
                      </span>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        ) : (
          <div className="text-center py-20 px-4 bg-gray-50/50 border border-dashed border-gray-200 rounded-3xl w-full">
            <div className="w-16 h-16 bg-white rounded-2xl shadow-sm border border-gray-100 flex items-center justify-center mx-auto mb-6">
              <PlusCircle className="w-8 h-8 text-primary-300" />
            </div>
            <h3 className="text-lg font-bold text-secondry-800">Start building your list</h3>
            <p className="text-gray-500 mt-2 max-w-sm mx-auto text-[14px] leading-relaxed font-medium">
              Search and add medicines above to create your personal quick-access prescription toolkit.
            </p>
          </div>
        )}
      </div>
    </div>
  );
};

export default Frequent;

